/**
 * One moment in full: summary, emotions, topics and IAB, setting, objects, brands, products, people,
 * brand safety and transcript. Accepts a full `MomentOut` (video detail) or a `MomentSearchHit` (explore).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play } from 'lucide-react';
import type { MomentOut, MomentSearchHit } from '@/api/types';
import { Badge, type BadgeTone } from '@/components/arc/badge/badge';
import { Button } from '@/components/arc/button/button';
import { EmptyState } from '@/components/arc/empty-state/empty-state';
import { fmtDuration, labelize } from '@/lib/format';
import styles from './moment-card.module.css';

export type CardMoment = MomentOut | MomentSearchHit;

interface NormalizedMoment {
  id: string;
  start_sec: number;
  end_sec: number;
  scene_summary: string;
  transcript: string;
  sentiment: string;
  emotions: string[];
  topics: string[];
  iab: string[];
  objects: string[];
  brands: { name: string; visibility?: string; confidence?: number }[];
  products: { name: string; category?: string; confidence?: number; bbox_hint?: string | null }[];
  people: { name: string | null; role: string; is_celebrity: boolean; confidence?: number }[];
  setting: string | null;
  activity: string | null;
  risk: string | null;
  garm: string | null;
  flags: string[];
  keyframe_url: string | null;
}

function isSearchHit(m: CardMoment): m is MomentSearchHit {
  return 'video_title' in m;
}

function normalize(m: CardMoment): NormalizedMoment {
  if (isSearchHit(m)) {
    return {
      id: m.id,
      start_sec: m.start_sec,
      end_sec: m.end_sec,
      scene_summary: m.scene_summary,
      transcript: m.transcript,
      sentiment: m.sentiment,
      emotions: [],
      topics: m.topics ?? [],
      iab: m.iab ?? [],
      objects: [],
      brands: (m.brands ?? []).map((name) => ({ name })),
      products: (m.products ?? []).map((name) => ({ name })),
      people: [],
      setting: m.setting || null,
      activity: m.activity || null,
      risk: m.risk || null,
      garm: null,
      flags: [],
      keyframe_url: m.keyframe_url ?? m.thumbnail_url ?? null,
    };
  }
  return {
    id: m.id,
    start_sec: m.start_sec,
    end_sec: m.end_sec,
    scene_summary: m.scene_summary,
    transcript: m.transcript,
    sentiment: m.sentiment,
    emotions: m.emotions ?? [],
    topics: m.topics ?? [],
    iab: m.iab_categories ?? [],
    objects: m.objects ?? [],
    brands: m.brands ?? [],
    products: m.products ?? [],
    people: m.people ?? [],
    setting: m.setting,
    activity: m.activity,
    risk: m.brand_safety?.risk ?? null,
    garm: m.brand_safety?.garm_category ?? null,
    flags: m.brand_safety?.flags ?? [],
    keyframe_url: m.keyframe_url ?? null,
  };
}

const SENTIMENT_TONE: Record<string, BadgeTone> = {
  positive: 'success',
  neutral: 'neutral',
  negative: 'danger',
};
const RISK_TONE: Record<string, BadgeTone> = { low: 'success', medium: 'warning', high: 'danger' };

export function SentimentBadge({ sentiment }: { sentiment: string | null | undefined }) {
  if (!sentiment) return null;
  return (
    <Badge size="sm" tone={SENTIMENT_TONE[sentiment] ?? 'neutral'}>
      {labelize(sentiment)}
    </Badge>
  );
}

export function RiskBadge({ risk }: { risk: string | null | undefined }) {
  if (!risk) return null;
  return (
    <Badge size="sm" tone={RISK_TONE[risk] ?? 'neutral'}>
      {labelize(risk)} risk
    </Badge>
  );
}

function Confidence({ value }: { value: number | undefined }) {
  if (value === undefined || value === null) return null;
  const pct = Math.round(Math.min(1, Math.max(0, value)) * 100);
  return (
    <span className={styles.confidence} aria-label={`${pct}% confidence`}>
      <span className={styles.meter} aria-hidden="true">
        <span className={styles.meterFill} style={{ width: `${pct}%` }} />
      </span>
      <span className="mono">{pct}%</span>
    </span>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Chips({ items, mono = false }: { items: string[]; mono?: boolean }) {
  if (items.length === 0) return <span className={styles.none}>None</span>;
  return (
    <div className={styles.chips}>
      {items.map((t) => (
        <Badge key={t} size="sm" className={mono ? 'mono' : undefined}>
          {t}
        </Badge>
      ))}
    </div>
  );
}

export interface MomentCardProps {
  moment: CardMoment | null | undefined;
  /** Position in the list, for the "3 of 12" marker. */
  index?: number;
  count?: number;
  /** Seek the player to a second. Shows a play control when given. */
  onSeek?: (sec: number) => void;
  /** Video title, shown as a heading (linked when `href` is set). */
  videoTitle?: string;
  href?: string;
  className?: string;
}

export function MomentCard({ moment, index, count, onSeek, videoTitle, href, className }: MomentCardProps) {
  const [fullTranscript, setFullTranscript] = useState(false);
  const rootClass = [styles.card, className].filter(Boolean).join(' ');

  if (!moment) {
    return (
      <div className={rootClass}>
        <EmptyState
          title="No moment selected"
          description="Choose a moment on the timeline or in the list to see what happens in it."
        />
      </div>
    );
  }

  const m = normalize(moment);
  const length = Math.max(0, Math.round(m.end_sec - m.start_sec));
  const range = `${fmtDuration(m.start_sec)} to ${fmtDuration(m.end_sec)}`;
  const longTranscript = m.transcript.length > 220;

  return (
    <article className={rootClass} aria-label={`Moment ${range}`}>
      <header className={styles.header}>
        <div className={styles.headerMain}>
          {index !== undefined && count !== undefined && (
            <span className={`${styles.position} mono`}>
              {index + 1} of {count}
            </span>
          )}
          <span className={`${styles.range} mono`}>
            {range} ({length}s)
          </span>
          {onSeek && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onSeek(m.start_sec)}
              aria-label={`Play from ${fmtDuration(m.start_sec)}`}
            >
              <Play size={16} strokeWidth={1.75} aria-hidden="true" />
              Play
            </Button>
          )}
        </div>
        <div className={styles.badges}>
          <SentimentBadge sentiment={m.sentiment} />
          <RiskBadge risk={m.risk} />
        </div>
      </header>

      <div className={styles.body}>
        {videoTitle && (
          <h3 className={styles.videoTitle}>{href ? <Link to={href}>{videoTitle}</Link> : videoTitle}</h3>
        )}
        {m.keyframe_url && <img src={m.keyframe_url} alt="" className={styles.keyframe} loading="lazy" />}
        <p className={styles.summary}>{m.scene_summary}</p>
        {m.emotions.length > 0 && <Chips items={m.emotions} />}

        <Section title="Topics and IAB">
          <Chips items={[...m.topics, ...m.iab]} />
        </Section>

        <Section title="Setting and activity">
          <dl className={styles.pairs}>
            <div>
              <dt>Setting</dt>
              <dd>{labelize(m.setting) || 'Unknown'}</dd>
            </div>
            <div>
              <dt>Activity</dt>
              <dd>{labelize(m.activity) || 'Unknown'}</dd>
            </div>
          </dl>
          {m.objects.length > 0 && <Chips items={m.objects} />}
        </Section>

        <Section title={`Brands (${m.brands.length})`}>
          {m.brands.length === 0 ? (
            <span className={styles.none}>No brands detected</span>
          ) : (
            <ul className={styles.list}>
              {m.brands.map((b) => (
                <li key={b.name}>
                  <span className={styles.listMain}>
                    <span className={styles.name}>{b.name}</span>
                    {b.visibility && <Badge size="sm">{labelize(b.visibility)}</Badge>}
                  </span>
                  <Confidence value={b.confidence} />
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title={`Products (${m.products.length})`}>
          {m.products.length === 0 ? (
            <span className={styles.none}>No products detected</span>
          ) : (
            <ul className={styles.list}>
              {m.products.map((p, i) => (
                <li key={`${p.name}-${i}`}>
                  <span className={styles.listMain}>
                    <span className={styles.name}>{p.name}</span>
                    {p.category && <span className="text-muted">{p.category}</span>}
                    {p.bbox_hint && <span className="text-muted">{p.bbox_hint}</span>}
                  </span>
                  <Confidence value={p.confidence} />
                </li>
              ))}
            </ul>
          )}
        </Section>

        {m.people.length > 0 && (
          <Section title={`People (${m.people.length})`}>
            <div className={styles.chips}>
              {m.people.map((p, i) => (
                <Badge key={i} size="sm" tone={p.is_celebrity ? 'info' : 'neutral'}>
                  {p.name ?? labelize(p.role)}
                  {p.name ? `, ${labelize(p.role).toLowerCase()}` : ''}
                  {p.is_celebrity ? ' (public figure)' : ''}
                </Badge>
              ))}
            </div>
          </Section>
        )}

        <Section title="Brand safety (GARM)">
          <div className={styles.chips}>
            <RiskBadge risk={m.risk} />
            {m.garm && <Badge size="sm">{m.garm}</Badge>}
            {m.flags.map((f) => (
              <Badge key={f} size="sm" tone="danger">
                {f}
              </Badge>
            ))}
            {!m.garm && m.flags.length === 0 && <span className={styles.none}>No flags</span>}
          </div>
        </Section>

        <Section title="Transcript">
          {m.transcript ? (
            <>
              <p
                className={styles.transcript}
                data-clamped={!fullTranscript && longTranscript ? '' : undefined}
              >
                {m.transcript}
              </p>
              {longTranscript && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setFullTranscript((s) => !s)}
                  aria-expanded={fullTranscript}
                >
                  {fullTranscript ? 'Show less' : 'Show full transcript'}
                </Button>
              )}
            </>
          ) : (
            <span className={styles.none}>No speech in this moment</span>
          )}
        </Section>
      </div>
    </article>
  );
}
